import {
  OverflowGroupClassification,
  TournamentFormat,
  type Group,
} from '@/types/tournament.model';
import { TV_PALETTE } from './tv-mode.constants';
import { useTVScale } from './TVScaleContext';
import { getTvTeamFaceSlots } from './tv-team-faces';
import { TVTeamAvatarStack } from './TVTeamAvatarStack';
import { computeGroupClassifiedCount } from '@/utils/fixture-utils';

interface Props {
  group: Group;
  groupName: string;
  accent: string;
  classifiedPerGroup?: number;
  overflowGroupClassification?: OverflowGroupClassification;
  format?: TournamentFormat;
}

export const TVGroupTable = ({
  group,
  groupName,
  accent,
  classifiedPerGroup = 2,
  overflowGroupClassification,
  format,
}: Props) => {
  const { s, getTeamName } = useTVScale();
  const isGroupOf4 = (group.teamList?.length ?? 0) === 4;
  const isExtraClassifierInGroupsOf4 =
    isGroupOf4 &&
    overflowGroupClassification ===
      OverflowGroupClassification.EXTRA_CLASSIFIER;
  const effectiveClassifiedPerGroup = computeGroupClassifiedCount(
    classifiedPerGroup,
    format,
    isExtraClassifierInGroupsOf4,
  );

  const sortedTeams = [...(group.teamList ?? [])].sort(
    (a, b) => (b.stats?.totalPoints ?? 0) - (a.stats?.totalPoints ?? 0),
  );

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: s(8),
        }}
      >
        <span
          className="font-gilroy"
          style={{
            fontSize: s(17),
            fontWeight: 800,
            color: accent,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          {groupName}
        </span>
        <span
          style={{
            fontSize: s(11),
            color: TV_PALETTE.textSubtle,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          Fase Clasificatoria
        </span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${TV_PALETTE.border}` }}>
            {['POS', 'EQUIPO', 'PJ', 'G', 'P', 'PTS'].map((h) => (
              <th
                key={h}
                style={{
                  fontSize: s(11),
                  color: TV_PALETTE.textSubtle,
                  fontWeight: 700,
                  textAlign: h === 'EQUIPO' ? 'left' : 'center',
                  padding: `${s(4)}px ${s(4)}px ${s(6)}px`,
                  letterSpacing: '0.08em',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedTeams.map((row, i) => {
            const isQualifier = i < effectiveClassifiedPerGroup;
            const isLeader = i === 0;
            return (
              <tr
                key={row.team?.id ?? i}
                style={{
                  borderBottom: `1px solid ${TV_PALETTE.borderSoft}`,
                  background: isQualifier
                    ? isLeader
                      ? TV_PALETTE.qualifierGreenBgLead
                      : TV_PALETTE.qualifierGreenBg
                    : 'transparent',
                }}
              >
                <td
                  style={{
                    padding: `${s(6)}px ${s(4)}px`,
                    textAlign: 'center',
                  }}
                >
                  <span
                    className="font-space"
                    style={{
                      fontSize: s(16),
                      fontWeight: 700,
                      color: isLeader
                        ? accent
                        : isQualifier
                          ? TV_PALETTE.textMuted
                          : TV_PALETTE.textGhost,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {i + 1}
                  </span>
                </td>
                <td style={{ padding: `${s(6)}px ${s(4)}px` }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: s(8),
                      minWidth: 0,
                    }}
                  >
                    <TVTeamAvatarStack
                      slots={getTvTeamFaceSlots(row.team)}
                      size={s(26)}
                      dimmed={!isQualifier}
                    />
                    <span
                      style={{
                        fontSize: s(13),
                        fontWeight: isQualifier ? 700 : 400,
                        color: isQualifier
                          ? TV_PALETTE.navy
                          : TV_PALETTE.textSubtle,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                      }}
                    >
                      {getTeamName(row.team)}
                    </span>
                  </div>
                </td>
                <td
                  className="font-space"
                  style={{
                    padding: `${s(6)}px ${s(4)}px`,
                    textAlign: 'center',
                    fontSize: s(15),
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                    color: TV_PALETTE.textMuted,
                  }}
                >
                  {row.stats?.matchesPlayed ?? 0}
                </td>
                <td
                  className="font-space"
                  style={{
                    padding: `${s(6)}px ${s(4)}px`,
                    textAlign: 'center',
                    fontSize: s(15),
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                    color: TV_PALETTE.textMuted,
                  }}
                >
                  {row.stats?.wins ?? 0}
                </td>
                <td
                  className="font-space"
                  style={{
                    padding: `${s(6)}px ${s(4)}px`,
                    textAlign: 'center',
                    fontSize: s(15),
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                    color: TV_PALETTE.textMuted,
                  }}
                >
                  {row.stats?.losses ?? 0}
                </td>
                <td
                  style={{
                    padding: `${s(6)}px ${s(4)}px`,
                    textAlign: 'center',
                  }}
                >
                  <span
                    className="font-space"
                    style={{
                      fontSize: s(21),
                      fontWeight: 700,
                      color: isLeader
                        ? accent
                        : isQualifier
                          ? TV_PALETTE.navy
                          : TV_PALETTE.textGhost,
                      fontVariantNumeric: 'tabular-nums',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {row.stats?.totalPoints ?? 0}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
